import _ from 'lodash';
import invariant from 'invariant';
import ChatMember from 'entities/ChatMember';
import Entity from 'lib/Entity';
import OfficeHours from 'entities/OfficeHours';
// @ts-ignore
import User from 'entities/User';
import Backbone from 'backbone-model';
import { Backbone$Id } from 'types/typescript/Backbone';
import { OfficeHoursParsable } from 'entities/OfficeHours';

type DefaultAttrs = {
  sort_key: string;
  group_ids: number[];
  group_names: string[];
  school_names: string[];
  note: string;
};

class PotentialChatMember extends Entity {
  initialize() {
    this.urlRoot = '/chats/members';
  }

  defaults(): DefaultAttrs {
    return {
      sort_key: 'name',
      group_ids: [],
      group_names: [],
      school_names: [],
      note: '',
    };
  }

  parse(response: { office_hours?: OfficeHoursParsable | null; user: User }) {
    const member = _.cloneDeep(response);
    member['office_hours'] = member['office_hours']
      ? OfficeHours.fromJSON(member['office_hours'])
      : null;
    member['user'] = User.fromJSON(member['user']);

    return member;
  }

  getName() {
    return this.get('name');
  }

  getUuid() {
    return this.get('uuid');
  }

  isMemberOfGroup(groupId: Backbone$Id) {
    return this.get('group_ids').includes(groupId);
  }

  isTeacherLead() {
    return this.get('record_type') === 'teacher_lead';
  }

  isRecommended() {
    return !!this.get('recommended');
  }

  isInvited() {
    return this.getState() === 'invited';
  }

  isInvitable() {
    return this.getState() === 'invitable';
  }

  getConnectionDescription() {
    return this.get('connection_description');
  }

  getFirstName() {
    return this.get('first_name');
  }

  getLastName() {
    return this.get('last_name');
  }

  getSortKey() {
    return this.get('sort_key');
  }

  getQueryKey() {
    return this.get('query_key');
  }

  setName(name: string): Backbone.Model {
    return this.set({ name });
  }

  getRole() {
    return this.get('role');
  }

  getState() {
    return this.get('state');
  }

  setState(state: string) {
    return this.set('state', state);
  }

  getGroupNames() {
    return this.get('group_names');
  }

  getSchoolNames() {
    return this.get('school_names');
  }

  getNote() {
    return this.get('note');
  }

  getOfficeHours() {
    return this.get('office_hours');
  }

  getUser(): User {
    return this.get('user');
  }

  isEligible() {
    return this.getState() === 'eligible';
  }

  isPending() {
    return this.getState() === 'pending';
  }

  isUnder13() {
    return this.getState() === 'under_13';
  }

  isUnavailable() {
    return this.getState() === 'unavailable';
  }

  isAgeUnknown() {
    return this.getState() === 'unknown';
  }

  requestAge() {
    invariant(
      this.isAgeUnknown(),
      'Age can only be requested for PotentialChatMember with unknown age.'
    );

    return (this.save(
      {
        state: 'pending',
      },
      {
        data: { member: { state: 'pending' } },
        patch: true,
      }
    ) as unknown) as Promise<PotentialChatMember>;
  }

  // eslint-disable-next-line no-use-before-define
  static getChatMember(user: PotentialOrRegularChatMember) {
    return new PotentialChatMember({
      id: user.id,
      name: user.getName(),
      role: user.getRole(),
      office_hours: user.getOfficeHours(),
    });
  }
}

export default PotentialChatMember;

export type PotentialOrRegularChatMember = PotentialChatMember | ChatMember;
